import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import ItemServiceApi from '../../Api/item-service';
import { UpdateKeyDetailsInputs } from '../../../Components/SellToUs/schemas/updateKeyDetails';
import toast from '../../../Components/Toast/Toast';
import { ContentType, VendorCatalogItem } from './types';
import { UseGetItemPhotoURLsReturnResponse } from './useGetItemPhotoURLs';

type UpdateItemKeyDetailsParams = {
  userId: string;
  itemId: string;
  updates: UpdateKeyDetailsInputs & {
    front_file?: File;
    swatch_file?: File;
  };
};

export const useUpdateKeyDetails = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: UpdateItemKeyDetailsParams) => {
      const {
        front_file: frontFile,
        swatch_file: swatchFile,
        ...toUpdate
      } = params.updates;
      const updateResponse = await ItemServiceApi.updateItemKeyDetails(
        params.userId,
        params.itemId,
        toUpdate,
      );
      if (updateResponse.front_photo_upload_url && frontFile) {
        const response = await axios({
          method: 'put',
          url: updateResponse.front_photo_upload_url,
          data: frontFile,
          headers: {
            'Content-Type': toUpdate.front_content_type,
          },
        });
        if (response.status !== 200 && response.status !== 201) {
          return {
            success: true,
            imageUploadSuccess: false,
            data: updateResponse,
          };
        }
      }
      if (updateResponse.swatch_photo_upload_url && swatchFile) {
        const response = await axios({
          method: 'put',
          url: updateResponse.swatch_photo_upload_url,
          data: swatchFile,
          headers: {
            'Content-Type': toUpdate.swatch_content_type,
          },
        });
        if (response.status !== 200 && response.status !== 201) {
          return {
            success: true,
            imageUploadSuccess: false,
            data: updateResponse,
          };
        }
      }
      return {
        success: true,
        imageUploadSuccess: true,
        data: updateResponse,
      };
    },
    {
      onSuccess: async (result, { userId, itemId, updates }) => {
        queryClient.setQueriesData<{ items: VendorCatalogItem[] } | undefined>(
          ['item-service-vendor-catalog', userId],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              items:
                oldData.items?.map((oldItem) => {
                  if (oldItem.id !== itemId) return oldItem;

                  return {
                    ...oldItem,
                    ...result.data.item,
                  };
                }) ?? [],
            };
          },
        );

        queryClient.setQueriesData<VendorCatalogItem | undefined>(
          ['item-service-vendor-catalog-item', userId, itemId],
          (oldItem) => {
            if (!oldItem || oldItem.id !== itemId) {
              return oldItem;
            }

            return {
              ...oldItem,
              ...result.data.item,
            };
          },
        );

        await queryClient.setQueriesData<
          UseGetItemPhotoURLsReturnResponse | undefined
        >(['item-service-photo-urls', userId, itemId], (oldData) => {
          if (!oldData || !result.imageUploadSuccess) return oldData;

          const newData = { ...oldData, photos: [...oldData.photos] };

          const frontPhotoUrl = result.data.front_photo_url;
          const swatchPhotoUrl = result.data.swatch_photo_url;

          if (frontPhotoUrl) {
            const wasFrontPhotoExisting = !!newData.photos.find(
              (photo) => photo.type === 'front',
            );
            if (wasFrontPhotoExisting) {
              newData.photos = newData.photos.map((photo) => {
                if (photo.type !== 'front') return photo;
                return {
                  ...photo,
                  url: frontPhotoUrl,
                  content_type: updates.front_content_type as ContentType,
                };
              });
            } else {
              newData.photos.push({
                type: 'front',
                url: frontPhotoUrl,
                content_type: updates.front_content_type as ContentType,
                is_netsuite_image: false,
              });
            }
          }

          if (swatchPhotoUrl) {
            const wasSwatchPhotoExisting = !!newData.photos.find(
              (photo) => photo.type === 'swatch',
            );
            if (wasSwatchPhotoExisting) {
              newData.photos = newData.photos.map((photo) => {
                if (photo.type !== 'swatch') return photo;
                return {
                  ...photo,
                  url: swatchPhotoUrl,
                  content_type: updates.swatch_content_type as ContentType,
                };
              });
            } else {
              newData.photos.push({
                type: 'swatch',
                url: swatchPhotoUrl,
                content_type: updates.swatch_content_type as ContentType,
                is_netsuite_image: false,
              });
            }
          }

          return newData;
        });

        if (!result.imageUploadSuccess) {
          toast(
            'Item key details have been successfully updated but some photos could not be uploaded. Please try again later. If it persists, please contact support.',
            {
              type: 'warning',
            },
          );
        } else {
          toast('Item key details have been successfully updated', {
            type: 'success',
          });
        }
      },
      onError: () => {
        toast(
          'An error occurred while updating the item key details. Please try again later. If it persists, please contact support.',
          { type: 'error' },
        );
      },
    },
  );
};
